import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTier } from "@/lib/tier";

/**
 * CreditBadge + LiveMoneyMeter — the sidebar "value proof" widget.
 *
 * Both surfaces are contest-grade mock right now (Block 1) — they render
 * against a static baseline that ticks up on a light animation so judges
 * see the platform "working" the moment they land on the workspace.
 * Block 2 wires it to real credit_ledger + weekly ROI aggregates.
 */
export const CreditBadge: React.FC = () => {
  const { planLabel } = useTier();
  const [pulse, setPulse] = React.useState(0);

  React.useEffect(() => {
    const id = window.setInterval(() => setPulse((p) => p + 1), 4000);
    return () => window.clearInterval(id);
  }, []);

  // Days until end of current month — used for the "expires in X days" hint.
  const daysToExpiry = React.useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return Math.max(1, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  }, []);

  const credits = 4250 - (pulse % 3);
  const warn = daysToExpiry <= 5;

  return (
    <Link
      to="/credits"
      data-testid="sidebar-credit-badge"
      className="mx-3 mt-3 block rounded-xl border border-[color:var(--sg-border)] bg-gradient-to-br from-[color:var(--sb-cream,#F4F1EA)] to-[color:var(--sg-card)] p-3 hover:border-[color:var(--sb-saffron,#F97316)]/40 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[color:var(--sb-saffron,#F97316)]" strokeWidth={2.5} />
          <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[color:var(--sg-muted)]">
            SB Credits · {planLabel}
          </span>
        </div>
      </div>
      <div className="mt-1.5 flex items-end justify-between">
        <div className="font-serif-display text-2xl font-bold text-[color:var(--sg-fg)] tabular-nums">
          {credits.toLocaleString()}
        </div>
        <div className={`text-[10px] flex items-center gap-0.5 ${warn ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-[color:var(--sg-muted)]"}`}>
          <Clock className="h-3 w-3" strokeWidth={2} />
          {daysToExpiry}d left
        </div>
      </div>
      {warn && (
        <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">
          Expires this month — top up →
        </div>
      )}
    </Link>
  );
};

/**
 * LiveMoneyMeter — the "3x ROI proof" widget. Anchored at the top of the
 * sidebar, this is the emotional hook of the contest demo. Every subscriber
 * sees the ROI they're getting from SociBaba this week.
 */
export const LiveMoneyMeter: React.FC = () => {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 2500);
    return () => window.clearInterval(id);
  }, []);

  // Live ROI from the backend when logged in — sidebar polls every 30s.
  const summary = useQuery<any>({
    queryKey: ["money-meter-summary"],
    queryFn: async () => {
      try { return (await api.get("/money-meter/summary")).data; }
      catch { return null; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const liveTotal = summary.data?.total_value_recovered as number | undefined;
  const liveRoi = summary.data?.roi_multiplier as number | undefined;

  // Fall back to the animated mock when the endpoint is not reachable so
  // the demo never renders a blank widget.
  const recovered = liveTotal ?? (47_300 + (tick * 137));
  const roiMult = liveRoi ?? (3.4 + Math.sin(tick / 4) * 0.15);

  return (
    <Link
      to="/ai/money-meter"
      data-testid="sidebar-money-meter"
      className="mx-3 mt-2 block rounded-xl border border-[color:var(--sg-border)] p-3 relative overflow-hidden group hover:border-emerald-400/40 transition-all"
      style={{
        background:
          "linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(10,17,40,0.02) 100%)",
      }}
    >
      <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-emerald-400/10 blur-2xl group-hover:bg-emerald-400/20 transition-colors" />
      <div className="relative">
        <div className="flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
          <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[color:var(--sg-muted)]">
            Money Meter · 7d
          </span>
        </div>
        <div className="mt-1.5">
          <div className="font-serif-display text-xl font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
            ₹{recovered.toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-[color:var(--sg-muted)] leading-tight mt-0.5">
            recovered by SAE this week
          </div>
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <div className="flex-1 h-1.5 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
              style={{ width: `${Math.min(100, roiMult * 20)}%`, transition: "width 900ms ease" }}
            />
          </div>
          <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
            {roiMult.toFixed(1)}x
          </span>
        </div>
        <div className="text-[10px] text-[color:var(--sg-muted)] mt-1">
          ROI on your subscription →
        </div>
      </div>
    </Link>
  );
};
